/**
 * OPC UA Proxy API 服务
 *
 * OPC UA Proxy 地址: http://localhost:3031
 */

const OPCUA_BASE = 'http://localhost:3031';

/**
 * 通用 OPC UA 请求封装
 */
async function opcuaRequest<T = any>(
  endpoint: string,
  options: { method?: string; body?: any } = {},
): Promise<T> {
  const { method = 'POST', body } = options;

  const fetchOptions: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };

  if (body) {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(`${OPCUA_BASE}${endpoint}`, fetchOptions);
  const data = await response.json();

  if (!data.success) {
    throw new Error(data.message || 'OPC UA 请求失败');
  }

  return data;
}

/**
 * 连接 OPC UA 服务器
 * POST /api/opcua/connect
 */
export interface ConnectParams {
  url: string; // OPC UA 服务器地址，如 opc.tcp://192.168.0.109:4840
}

export interface ConnectResult {
  success: boolean;
  message: string;
}

export async function opcuaConnect(params: ConnectParams): Promise<ConnectResult> {
  return opcuaRequest<ConnectResult>('/api/opcua/connect', {
    method: 'POST',
    body: params,
  });
}

/**
 * 断开 OPC UA 连接
 * POST /api/opcua/disconnect
 */
export interface DisconnectResult {
  success: boolean;
  message: string;
}

export async function opcuaDisconnect(): Promise<DisconnectResult> {
  return opcuaRequest<DisconnectResult>('/api/opcua/disconnect', {
    method: 'POST',
  });
}

/**
 * 递归浏览节点树
 * 一次调用返回所有层级 Variable 节点
 * POST /api/opcua/browse-tree
 */
export interface BrowseTreeParams {
  nodeId?: string;   // 起始节点 ID，默认 "Objects"
  maxDepth?: number; // 最大递归层数，默认 5
}

export interface BrowseTreeNode {
  id: string;
  name: string;
  displayName: string;
  nodeClass: 'Object' | 'Variable';
  hasChildren: boolean;
}

export interface BrowseTreeResult {
  success: boolean;
  value: string;      // JSON 字符串，需 JSON.parse 解析为 BrowseTreeNode[]
  totalCount: number;
}

export async function opcuaBrowseTree(
  params: BrowseTreeParams = {},
): Promise<BrowseTreeResult> {
  return opcuaRequest<BrowseTreeResult>('/api/opcua/browse-tree', {
    method: 'POST',
    body: params,
  });
}

/**
 * 读取单个点位值
 * POST /api/opcua/read
 */
export interface ReadParams {
  nodeId: string; // 点位 ID，如 ns=2;s=xxx
}

export interface ReadResult {
  success: boolean;
  value: string;  // 点位数值（字符串形式）
  dataType: string;
}

export async function opcuaRead(params: ReadParams): Promise<ReadResult> {
  return opcuaRequest<ReadResult>('/api/opcua/read', {
    method: 'POST',
    body: params,
  });
}

/**
 * 读取历史数据
 * POST /api/opcua/historical
 */
export interface HistoricalParams {
  nodeId: string;    // 点位 ID
  startTime: string; // ISO 8601，如 2025-06-01T00:00:00Z
  endTime: string;   // 同上
}

export interface HistoricalDataPoint {
  value: {
    dataType: string;
    arrayType: string;
    value: any;
  };
  statusCode: {
    value: number;   // 0=Good
  };
  sourceTimestamp: string;
}

export interface HistoricalResult {
  success: boolean;
  data: HistoricalDataPoint[];
}

export async function opcuaHistorical(params: HistoricalParams): Promise<HistoricalResult> {
  return opcuaRequest<HistoricalResult>('/api/opcua/historical', {
    method: 'POST',
    body: params,
  });
}

// ============================================================
// 二、连接配置 CRUD（前缀 /api/connections）
// ============================================================

/** 连接配置数据结构 */
export interface OpcConnection {
  id: string;
  name: string;
  server_url: string;
  username: string;
  password: string;
  auto_connect: number;   // 0/1
  auto_reconnect: number;  // 0/1
}

/** 创建/更新连接参数 */
export type OpcConnectionInput = Omit<OpcConnection, 'id'>;

/**
 * 获取全部连接配置
 * GET /api/connections
 */
export async function opcGetConnections(): Promise<OpcConnection[]> {
  const res = await opcuaRequest<{ success: boolean; data: OpcConnection[] }>(
    '/api/connections',
    { method: 'GET' },
  );
  return res.data || [];
}

/**
 * 获取单个连接配置
 * GET /api/connections/:id
 */
export async function opcGetConnection(id: string): Promise<OpcConnection | null> {
  const res = await opcuaRequest<{ success: boolean; data: OpcConnection }>(
    `/api/connections/${id}`,
    { method: 'GET' },
  );
  return res.data || null;
}

/**
 * 创建连接配置
 * POST /api/connections
 * 注意：同 server_url 重复创建不会新建记录，会更新已有连接
 */
export async function opcCreateConnection(data: OpcConnectionInput): Promise<OpcConnection> {
  const res = await opcuaRequest<{ success: boolean; data: OpcConnection }>(
    '/api/connections',
    { method: 'POST', body: data },
  );
  return res.data;
}

/**
 * 更新连接配置
 * PUT /api/connections/:id
 */
export async function opcUpdateConnection(
  id: string,
  data: Partial<OpcConnectionInput>,
): Promise<OpcConnection> {
  const res = await opcuaRequest<{ success: boolean; data: OpcConnection }>(
    `/api/connections/${id}`,
    { method: 'PUT', body: data },
  );
  return res.data;
}

/**
 * 删除连接配置（级联删除分组和点位关联）
 * DELETE /api/connections/:id
 */
export async function opcDeleteConnection(id: string): Promise<void> {
  await opcuaRequest<{ success: boolean }>(
    `/api/connections/${id}`,
    { method: 'DELETE' },
  );
}

// ============================================================
// 三、点位分组 CRUD（前缀 /api/connections/:connectionId/groups）
// ============================================================

export interface OpcGroup {
  id: string;
  name: string;
  icon: string;
  is_default: number;  // 0/1，默认分组不可删除/重命名
  point_count: number;
}

/**
 * 获取连接下所有分组
 * GET /api/connections/:connectionId/groups
 */
export async function opcGetGroups(connectionId: string): Promise<OpcGroup[]> {
  const res = await opcuaRequest<{ success: boolean; data: OpcGroup[] }>(
    `/api/connections/${connectionId}/groups`,
    { method: 'GET' },
  );
  return res.data || [];
}

/**
 * 创建分组
 * POST /api/connections/:connectionId/groups
 */
export async function opcCreateGroup(
  connectionId: string,
  data: { name: string; icon: string },
): Promise<OpcGroup> {
  const res = await opcuaRequest<{ success: boolean; data: OpcGroup }>(
    `/api/connections/${connectionId}/groups`,
    { method: 'POST', body: data },
  );
  return res.data;
}

/**
 * 更新分组
 * PUT /api/connections/:connectionId/groups/:id
 */
export async function opcUpdateGroup(
  connectionId: string,
  groupId: string,
  data: { name?: string; icon?: string },
): Promise<OpcGroup> {
  const res = await opcuaRequest<{ success: boolean; data: OpcGroup }>(
    `/api/connections/${connectionId}/groups/${groupId}`,
    { method: 'PUT', body: data },
  );
  return res.data;
}

/**
 * 删除分组（级联删除点位关联）
 * DELETE /api/connections/:connectionId/groups/:id
 */
export async function opcDeleteGroup(
  connectionId: string,
  groupId: string,
): Promise<void> {
  await opcuaRequest<{ success: boolean }>(
    `/api/connections/${connectionId}/groups/${groupId}`,
    { method: 'DELETE' },
  );
}

// ============================================================
// 四、分组-点位关联
// ============================================================

/**
 * 获取分组内点位 ID 列表
 * GET /api/connections/:connectionId/groups/:id/points
 */
export async function opcGetGroupPoints(
  connectionId: string,
  groupId: string,
): Promise<{ pointIds: string[]; total: number }> {
  const res = await opcuaRequest<{ success: boolean; data: string[]; total: number }>(
    `/api/connections/${connectionId}/groups/${groupId}/points`,
    { method: 'GET' },
  );
  return { pointIds: res.data || [], total: res.total || 0 };
}

/**
 * 批量添加点位到分组
 * POST /api/connections/:connectionId/groups/:id/points
 */
export async function opcAddPointsToGroup(
  connectionId: string,
  groupId: string,
  pointIds: string[],
): Promise<number> {
  const res = await opcuaRequest<{ success: boolean; count: number }>(
    `/api/connections/${connectionId}/groups/${groupId}/points`,
    { method: 'POST', body: { point_ids: pointIds } },
  );
  return res.count || 0;
}

/**
 * 从分组移除点位
 * DELETE /api/connections/:connectionId/groups/:id/points
 */
export async function opcRemovePointsFromGroup(
  connectionId: string,
  groupId: string,
  pointIds: string[],
): Promise<number> {
  const res = await opcuaRequest<{ success: boolean; count: number }>(
    `/api/connections/${connectionId}/groups/${groupId}/points`,
    { method: 'DELETE', body: { point_ids: pointIds } },
  );
  return res.count || 0;
}

/**
 * 查询点位属于哪些分组
 * GET /api/groups-of-point?connection_id=xxx&point_id=xxx
 */
export async function opcGetGroupsOfPoint(
  connectionId: string,
  pointId: string,
): Promise<string[]> {
  const res = await opcuaRequest<{ success: boolean; data: string[] }>(
    `/api/groups-of-point?connection_id=${encodeURIComponent(connectionId)}&point_id=${encodeURIComponent(pointId)}`,
    { method: 'GET' },
  );
  return res.data || [];
}

/**
 * 将单个点位添加到指定分组（已存在则忽略）
 * POST /api/connections/:connectionId/groups/:id/points
 */
export async function opcSetPointGroup(
  connectionId: string,
  pointId: string,
  targetGroupId: string | null,
): Promise<void> {
  if (targetGroupId) {
    await opcAddPointsToGroup(connectionId, targetGroupId, [pointId]);
  }
}

/**
 * 批量添加点位到指定分组（已存在则忽略）
 * POST /api/connections/:connectionId/groups/:id/points
 */
export async function opcBatchSetPointsGroup(
  connectionId: string,
  pointIds: string[],
  targetGroupId: string | null,
): Promise<void> {
  if (targetGroupId) {
    await opcAddPointsToGroup(connectionId, targetGroupId, pointIds);
  }
}
